import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import DatePicker from 'react-datepicker';
import GuestPetSelector from '../components/GuestPetSelector';
import { savePropertyActivity } from '../utils/propertyActivities';
import 'react-datepicker/dist/react-datepicker.css';
import { 
  FaMapMarkerAlt, 
  FaStar, 
  FaWifi, 
  FaParking, 
  FaSwimmingPool,
  FaUtensils,
  FaDumbbell,
  FaCheck,
  FaTimes,
  FaInfoCircle,
  FaCalendarAlt,
  FaClock,
  FaUsers,
  FaArrowLeft,
  FaChevronLeft,
  FaChevronRight,
  FaExpand,
  FaSpinner,
  FaHeart,
  FaChevronRight as FaChevronRightIcon,
  FaBriefcase,
  FaUser,
  FaUserFriends,
  FaHeart as FaCouple,
  FaBus,
  FaTrain,
  FaShoppingBag,
  FaLandmark,
  FaQuestionCircle,
  FaBed,
  FaBath,
  FaHome,
  FaConciergeBell,
  FaTv,
  FaSnowflake,
  FaCoffee,
  FaWind,
  FaDoorOpen,
  FaCouch,
  FaShower,
  FaSoap,
  FaFan,
  FaSmokingBan,
  FaTree,
  FaRupeeSign,
  FaCreditCard,
  FaCheckCircle
} from 'react-icons/fa';
import PropertyLocationMap from '../components/PropertyLocationMap';
import LoginModal from '../components/LoginModal';
import TimePickerModal from '../components/TimePickerModal';
import { getPropertyImages, getImageUrl } from '../utils/imageHelper';
import { getApiUrl } from '../config/api';
import { toast } from 'react-toastify';
import { getFavoriteId, loadWishlistFavorites, saveWishlistFavorites } from '../utils/wishlistStorage';
import { isDiscountVisibleToGuest } from '../utils/offerStatus';
import './PropertyDetailsPage.css';
import './PropertyDetailsPage-map.css';
import './PropertyDetailsPage-qa.css';
import './PropertyDetailsPage-facilities.css';

const PropertyDetailsPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [property, setProperty] = useState(null);
  
  // Helper function to safely get string value from property field
  const getSafeString = (value, fallback = '') => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      // If it's an object, try to extract meaningful data
      if (value.name) return String(value.name);
      if (value.value) return String(value.value);
      return fallback;
    }
    return String(value);
  };

  const normalizeTimeTo24Hour = (rawValue, fallback = '') => {
    if (!rawValue || typeof rawValue !== 'string') return fallback;
    const source = rawValue.trim();
    if (!source) return fallback;

    const hhmmMatch = source.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
    if (hhmmMatch) {
      const hours = String(Number(hhmmMatch[1])).padStart(2, '0');
      return `${hours}:${hhmmMatch[2]}`;
    }

    const tokenMatch = source.match(/(\d{1,2}):([0-5]\d)\s*(AM|PM)/i);
    if (tokenMatch) {
      let hours = Number(tokenMatch[1]) % 12;
      const minutes = tokenMatch[2];
      if (tokenMatch[3].toUpperCase() === 'PM') hours += 12;
      return `${String(hours).padStart(2, '0')}:${minutes}`;
    }

    return fallback;
  };

  const formatTimeForDisplay = (timeValue, placeholder = 'Select time') => {
    const normalized = normalizeTimeTo24Hour(timeValue);
    if (!normalized) return placeholder;
    const [hours, minutes] = normalized.split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return placeholder;
    const displayDate = new Date();
    displayDate.setHours(hours, minutes, 0, 0);
    return displayDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const buildIsoFromDateAndTime = (dateValue, timeValue) => {
    const date = dateValue instanceof Date ? new Date(dateValue) : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;
    const normalizedTime = normalizeTimeTo24Hour(timeValue);
    if (normalizedTime) {
      const [hours, minutes] = normalizedTime.split(':').map(Number);
      date.setHours(hours, minutes, 0, 0);
    }
    return date.toISOString();
  };

  const [loading, setLoading] = useState(true);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [checkInTime, setCheckInTime] = useState('');
  const [checkOutTime, setCheckOutTime] = useState('');
  const [timePickerOpenFor, setTimePickerOpenFor] = useState(null); // 'checkIn' | 'checkOut' | null
  const [scheduleMonthStart, setScheduleMonthStart] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [guests, setGuests] = useState(2);
  const [guestData, setGuestData] = useState({
    adults: 2,
    children: 0,
    infants: 0,
    rooms: 1,
    pets: 0
  });
  const [showGuestSelector, setShowGuestSelector] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [stayBookedSlots, setStayBookedSlots] = useState([]);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [reserveLoading, setReserveLoading] = useState(false);
  const [reserveSuccess, setReserveSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [isSaved, setIsSaved] = useState(false);
  const [similarAccommodations, setSimilarAccommodations] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  
  // Ask Question State
  const [question, setQuestion] = useState('');
  const [questionSubmitting, setQuestionSubmitting] = useState(false);
  const [questionSubmitted, setQuestionSubmitted] = useState(false);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  // Property Q&A from API (host-answered questions only)
  const [propertyQA, setPropertyQA] = useState([]);
  const [loadingQA, setLoadingQA] = useState(false);
  // Property Reviews
  const [propertyReviews, setPropertyReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  useEffect(() => {
    // Load current user
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setCurrentUser(JSON.parse(storedUser));
      }
    } catch (error) {
      console.error('Error loading user:', error);
    }
  }, []);

  useEffect(() => {
    // ✅ SECURITY: Check if property is already in user-specific favorites
    if (property) {
      try {
        // Get user-specific favorites key
        const getFavoritesKey = () => {
          try {
            const user = localStorage.getItem('user');
            if (user) {
              const userObj = JSON.parse(user);
              return `favorites_${userObj._id || userObj.id}`;
            }
          } catch (error) {
            console.error('Error getting user ID:', error);
          }
          return 'favorites'; // Fallback for non-logged-in users
        };
        
        const favoritesKey = getFavoritesKey();
        const favorites = loadWishlistFavorites(favoritesKey);
        const propertyId = property.id || property._id;
        const isFavorite = favorites.some(fav => getFavoriteId(fav) === propertyId);
        setIsSaved(isFavorite);
      } catch (error) {
        console.error('Error checking favorites:', error);
      }
    }
  }, [property]);

  useEffect(() => {
    if (!property) return;
    // Reset times when property changes so the dedicated "Select check-in time"
    // / "Select check-out time" buttons are visible until the user picks one.
    setCheckInTime('');
    setCheckOutTime('');
  }, [property, property?.id, property?._id]);

  useEffect(() => {
    // Fetch property details
    const fetchProperty = async () => {
      setLoading(true);
      try {
        // First try to get property from localStorage search results for quick loading
        const searchResults = localStorage.getItem('searchResults');
        let foundProperty = null;
        
        if (searchResults) {
          const results = JSON.parse(searchResults);
          const allProperties = results.results || [];
          foundProperty = allProperties.find(p => p.id === propertyId || p._id === propertyId);
        }
        
        // If found in localStorage, set it immediately for quick display
        if (foundProperty) {
          setProperty(foundProperty);
        }
        
        // Always fetch fresh data from API to get complete information including discounts
        try {
          const response = await fetch(getApiUrl('/api/properties/all'));
          const result = await response.json();
          
          if (result.success && result.properties) {
            const apiProperty = result.properties.find(p => p._id === propertyId || p.id === propertyId);
            if (apiProperty) {
              setProperty(apiProperty);
              foundProperty = apiProperty;
            }
          }
          // If still not found (e.g. direct link or inactive), fetch single property by ID for full data including address
          if (!foundProperty && propertyId) {
            const singleRes = await fetch(getApiUrl(`/api/properties/${propertyId}`));
            const singleData = await singleRes.json();
            if (singleData.success && singleData.property) {
              setProperty(singleData.property);
              foundProperty = singleData.property;
            }
          }
        } catch (apiError) {
          console.error('Error fetching property from API:', apiError);
          // Continue with localStorage data if API fails
        }
        
        if (foundProperty) {
          // Save property view activity
          try {
            // Get search data from URL params or localStorage
            const searchParams = new URLSearchParams(location.search);
            const lastSearchKey = (() => {
              try {
                const user = localStorage.getItem('user');
                if (user) {
                  const userObj = JSON.parse(user);
                  return `lastSearch_${userObj._id || userObj.id}`;
                }
              } catch (error) {
                console.error('Error getting user ID:', error);
              }
              return 'lastSearch';
            })();
            
            const lastSearch = localStorage.getItem(lastSearchKey);
            let searchData = null;

            const applySearchContext = (checkInRaw, checkOutRaw, guestsRaw) => {
              if (!checkInRaw || !checkOutRaw) return null;
              const start = new Date(checkInRaw);
              const end = new Date(checkOutRaw);
              if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
                return null;
              }

              setCheckIn(start);
              setCheckOut(end);

              const guestsCount = Number.parseInt(guestsRaw, 10);
              if (Number.isFinite(guestsCount) && guestsCount > 0) {
                setGuests(guestsCount);
                setGuestData({
                  adults: guestsCount,
                  children: 0,
                  infants: 0,
                  rooms: 1,
                  pets: 0
                });
              }

              const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
              return {
                checkIn: checkInRaw,
                checkOut: checkOutRaw,
                nights: nights > 0 ? nights : null,
                guests: Number.isFinite(guestsCount) && guestsCount > 0 ? guestsCount : null
              };
            };

            // Priority: URL params -> location.state -> searchResults -> lastSearch
            const checkInParam = searchParams.get('checkIn');
            const checkOutParam = searchParams.get('checkOut');
            if (checkInParam && checkOutParam) {
              searchData = applySearchContext(checkInParam, checkOutParam, searchParams.get('guests'));
            }

            if (!searchData && location.state?.searchParams?.checkIn && location.state?.searchParams?.checkOut) {
              searchData = applySearchContext(
                location.state.searchParams.checkIn,
                location.state.searchParams.checkOut,
                location.state.searchParams.guests?.adults || location.state.searchParams.guests
              );
            }

            if (!searchData) {
              try {
                const rawSearchResults = localStorage.getItem('searchResults');
                const parsedSearchResults = rawSearchResults ? JSON.parse(rawSearchResults) : null;
                const params = parsedSearchResults?.searchParams;
                if (params?.checkIn && params?.checkOut) {
                  searchData = applySearchContext(
                    params.checkIn,
                    params.checkOut,
                    params.guests?.adults || params.guests
                  );
                }
              } catch (e) {
                // Ignore parsing errors
              }
            }

            if (!searchData && lastSearch) {
              try {
                const parsed = JSON.parse(lastSearch);
                if (parsed.checkIn && parsed.checkOut) {
                  searchData = applySearchContext(
                    parsed.checkIn,
                    parsed.checkOut,
                    parsed.guests?.adults || parsed.guests
                  );
                }
              } catch (e) {
                // Ignore parsing errors
              }
            }
            
            savePropertyActivity(foundProperty, searchData);
          } catch (error) {
            console.error('Error saving property activity:', error);
          }
        } else {
          // Property not found
          console.error('Property not found');
        }
      } catch (error) {
        console.error('Error fetching property:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [propertyId]);

  // Scroll to top when propertyId changes or location state indicates scroll
  useEffect(() => {
    if (propertyId) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [propertyId]);

  // Handle scroll from location state (when navigating from similar accommodations)
  useEffect(() => {
    if (location.state?.scrollToTop) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [location.state]);

  // Fetch similar accommodations (same host or same location)
  useEffect(() => {
    const fetchSimilarAccommodations = async () => {
      if (!property) return;
      
      setLoadingSimilar(true);
      try {
        let similar = [];
        const searchUrl = getApiUrl('/api/search');
        
        // Fetch properties from same city (and same host) using search API
        if (property.city) {
          try {
            const searchQuery = property.city.trim();
            const response = await fetch(`${searchUrl}?q=${encodeURIComponent(searchQuery)}&limit=50`);
            const data = await response.json();
            
            console.log('Similar accommodations search results:', data);
            
            if (data.success && data.results) {
              const currentId = propertyId?.toString();
              
              console.log('Current property ID:', currentId);
              console.log('Total results:', data.results.length);
              
              const currentHostId = (property.hostId?._id ?? property.hostId)?.toString();
              
              // Filter: only actual (host-uploaded) accommodations; exclude current; same host OR same area
              const filtered = data.results.filter(prop => {
                if (prop.source && prop.source !== 'host') return false;
                const propId = (prop._id || prop.id)?.toString();
                if (propId === currentId) return false;
                
                const propHostId = (prop.hostId?._id ?? prop.hostId)?.toString();
                const sameHost = Boolean(currentHostId && propHostId && currentHostId === propHostId);
                const sameLocation = property.city && prop.city &&
                  prop.city.toLowerCase().trim() === property.city.toLowerCase().trim();
                
                return sameHost || sameLocation;
              });
              
              console.log('Filtered results:', filtered.length);
              
              // Sort: same host first, then same location
              filtered.sort((a, b) => {
                const aHostId = (a.hostId?._id ?? a.hostId)?.toString();
                const bHostId = (b.hostId?._id ?? b.hostId)?.toString();
                const aIsSameHost = currentHostId && aHostId && aHostId === currentHostId;
                const bIsSameHost = currentHostId && bHostId && bHostId === currentHostId;
                
                if (aIsSameHost && !bIsSameHost) return -1;
                if (!aIsSameHost && bIsSameHost) return 1;
                return 0;
              });
              
              // Remove duplicates and limit to 6
              const unique = [];
              const seenIds = new Set();
              
              for (const prop of filtered) {
                const propId = (prop._id || prop.id)?.toString();
                if (!seenIds.has(propId)) {
                  seenIds.add(propId);
                  unique.push(prop);
                  if (unique.length >= 6) break;
                }
              }
              
              similar = unique;
              console.log('Final similar accommodations:', similar.length);
            }
          } catch (err) {
            console.error('Error fetching similar accommodations:', err);
          }
        }
        
        setSimilarAccommodations(similar);
      } catch (error) {
        console.error('Error fetching similar accommodations:', error);
        setSimilarAccommodations([]);
      } finally {
        setLoadingSimilar(false);
      }
    };

    if (property) {
      fetchSimilarAccommodations();
    }
  }, [property, propertyId]);

  // Fetch property Q&A (host-answered questions only)
  const getQaUrl = (path) => getApiUrl(path);
  useEffect(() => {
    if (!propertyId) return;
    const fetchQA = async () => {
      setLoadingQA(true);
      try {
        const url = getQaUrl(`/api/properties/${propertyId}/qa`);
        const res = await fetch(url);
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          setPropertyQA([]);
          return;
        }
        const data = await res.json();
        if (data.success && Array.isArray(data.questions)) {
          setPropertyQA(data.questions);
        } else {
          setPropertyQA([]);
        }
      } catch (err) {
        console.error('Error fetching property Q&A:', err);
        setPropertyQA([]);
      } finally {
        setLoadingQA(false);
      }
    };
    fetchQA();
  }, [propertyId]);

  // Fetch property reviews
  useEffect(() => {
    if (!propertyId) return;
    const fetchReviews = async () => {
      setLoadingReviews(true);
      try {
        const url = getQaUrl(`/api/properties/${propertyId}/reviews`);
        const res = await fetch(url);
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          setPropertyReviews([]);
          return;
        }
        const data = await res.json();
        if (data.success && Array.isArray(data.reviews)) {
          setPropertyReviews(data.reviews);
        } else {
          setPropertyReviews([]);
        }
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setPropertyReviews([]);
      } finally {
        setLoadingReviews(false);
      }
    };
    fetchReviews();
  }, [propertyId]);

  const handleSubmitReview = useCallback(async () => {
    if (!propertyId || !currentUser || reviewRating < 1 || reviewSubmitting) return;
    const token = localStorage.getItem('token');
    if (!token) {
      toast.warning('Please log in to leave a review.');
      return;
    }
    setReviewSubmitting(true);
    try {
      const url = getQaUrl(`/api/properties/${propertyId}/reviews`);
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rating: reviewRating, comment: reviewComment.trim() }),
      });
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        toast.error('Server error. Please try again.');
        return;
      }
      const data = await res.json();
      if (data.success) {
        setReviewSubmitted(true);
        setReviewRating(0);
        setReviewComment('');
        const reviewsUrl = getQaUrl(`/api/properties/${propertyId}/reviews`);
        const revRes = await fetch(reviewsUrl);
        if (revRes.ok) {
          const revData = await revRes.json();
          if (revData.success && Array.isArray(revData.reviews)) {
            setPropertyReviews(revData.reviews);
          }
        }
      } else {
        toast.error(data.message || 'Failed to submit review.');
      }
    } catch (err) {
      console.error('Submit review error:', err);
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setReviewSubmitting(false);
    }
  }, [propertyId, currentUser, reviewRating, reviewComment, reviewSubmitting]);

  const qaOwnerInfo = useMemo(() => {
    if (!property) {
      return { name: 'Owner', email: 'Not available' };
    }

    const hostObject = property.hostId && typeof property.hostId === 'object' ? property.hostId : null;
    const ownerName = hostObject?.name || property.hostName || property.ownerName || 'Owner';
    const ownerEmail = hostObject?.email || property.hostEmail || property.ownerEmail || 'Not available';

    return {
      name: ownerName,
      email: ownerEmail
    };
  }, [property]);

  const numberOfNights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    const start = checkIn instanceof Date ? checkIn : new Date(checkIn);
    const end = checkOut instanceof Date ? checkOut : new Date(checkOut);
    const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return nights > 0 ? nights : 0;
  }, [checkIn, checkOut]);

  const totalPrice = useMemo(() => {
    if (!property || numberOfNights === 0) return 0;
    return (property.pricePerNight || 0) * numberOfNights;
  }, [property, numberOfNights]);

  useEffect(() => {
    if (!checkIn || Number.isNaN(new Date(checkIn).getTime())) return;
    const selected = new Date(checkIn);
    setScheduleMonthStart(new Date(selected.getFullYear(), selected.getMonth(), 1));
  }, [checkIn]);

  const availabilityBoard = useMemo(() => {
    const weekDays = [
      { key: 'sunday', label: 'Sun' },
      { key: 'monday', label: 'Mon' },
      { key: 'tuesday', label: 'Tue' },
      { key: 'wednesday', label: 'Wed' },
      { key: 'thursday', label: 'Thu' },
      { key: 'friday', label: 'Fri' },
      { key: 'saturday', label: 'Sat' }
    ];

    const formatSlotDate = (dateValue) => {
      if (!dateValue) return '';
      const parsedDate = dateValue instanceof Date ? dateValue : new Date(dateValue);
      if (Number.isNaN(parsedDate.getTime())) return '';
      const y = parsedDate.getFullYear();
      const m = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const d = String(parsedDate.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    const parseTimeToMinutes = (value, fallback) => {
      if (!value || typeof value !== 'string') return fallback;
      const normalized = value.trim().toUpperCase();
      const simpleMatch = normalized.match(/^(\d{1,2}):(\d{2})$/);
      if (simpleMatch) {
        const hours = Number(simpleMatch[1]);
        const minutes = Number(simpleMatch[2]);
        if (Number.isFinite(hours) && Number.isFinite(minutes)) {
          return (hours * 60) + minutes;
        }
      }

      const amPmMatch = normalized.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
      if (amPmMatch) {
        let hours = Number(amPmMatch[1]) % 12;
        const minutes = Number(amPmMatch[2]);
        const suffix = amPmMatch[3];
        if (suffix === 'PM') hours += 12;
        if (Number.isFinite(hours) && Number.isFinite(minutes)) {
          return (hours * 60) + minutes;
        }
      }
      return fallback;
    };

    const monthYear = new Date(scheduleMonthStart);
    const year = monthYear.getFullYear();
    const month = monthYear.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const now = new Date();
    const todayKey = formatSlotDate(now);

    const dayAvailability = property?.availability && typeof property.availability === 'object'
      ? property.availability
      : {};

    const getDayWindow = (dateValue) => {
      const parsedDate = dateValue instanceof Date ? dateValue : new Date(dateValue);
      if (Number.isNaN(parsedDate.getTime())) return null;
      const dayConfig = dayAvailability[weekDays[parsedDate.getDay()]?.key];
      if (dayConfig?.available === false) return null;

      const startMinutes = parseTimeToMinutes(dayConfig?.startTime, 0);
      const endMinutes = parseTimeToMinutes(dayConfig?.endTime, 24 * 60);
      if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes) || endMinutes <= startMinutes) {
        return null;
      }
      return { startMinutes, endMinutes };
    };

    const dayLabels = Array.from({ length: daysInMonth }, (_, index) => {
      const displayDate = new Date(year, month, index + 1);
      const dateKey = formatSlotDate(displayDate);
      return {
        key: dateKey,
        dateObj: displayDate,
        slotDate: dateKey,
        dayLabel: displayDate.toLocaleDateString('en-IN', { weekday: 'short' }),
        dateLabel: displayDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
        isToday: dateKey === todayKey,
        dayWindow: getDayWindow(displayDate)
      };
    });

    const slots = Array.from({ length: 24 }, (_, hour) => {
      const suffix = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return {
        key: `${String(hour).padStart(2, '0')}:00`,
        label: `${hour12}:00 ${suffix}`,
        hour,
        startMinutes: hour * 60,
        endMinutes: (hour + 1) * 60
      };
    });

    const allBookedSlots = [
      ...(Array.isArray(property?.bookedSlots) ? property.bookedSlots : []),
      ...(Array.isArray(property?.availability?.bookedSlots) ? property.availability.bookedSlots : []),
      ...(Array.isArray(availability?.bookedSlots) ? availability.bookedSlots : []),
      ...(Array.isArray(stayBookedSlots) ? stayBookedSlots : [])
    ];

    const bookedRanges = [];
    const addRange = (startRaw, endRaw) => {
      const start = startRaw instanceof Date ? startRaw : new Date(startRaw);
      const end = endRaw instanceof Date ? endRaw : new Date(endRaw);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) return;
      bookedRanges.push({ start, end });
    };

    allBookedSlots.forEach((slot) => {
      if (!slot) return;
      if (typeof slot === 'string') {
        const match = slot.match(/^(\d{4}-\d{2}-\d{2})-(\d{1,2})-(\d{1,2})$/);
        if (match) {
          const dateKey = match[1];
          const hour = Number(match[2]);
          const minute = Number(match[3]);
          const start = new Date(`${dateKey}T00:00:00`);
          start.setHours(hour, minute, 0, 0);
          const end = new Date(start.getTime() + 60 * 60 * 1000);
          addRange(start, end);
        }
        return;
      }

      if (Array.isArray(slot.bookings)) {
        slot.bookings.forEach((booking) => {
          addRange(booking?.checkIn || booking?.startTime, booking?.checkOut || booking?.endTime);
        });
        return;
      }

      if (slot.startTime && slot.endTime) {
        addRange(slot.startTime, slot.endTime);
        return;
      }

      if (slot.checkIn && slot.checkOut) {
        addRange(slot.checkIn, slot.checkOut);
      }
    });

    const getSlotStatus = (slotIndex, dayIndex) => {
      const day = dayLabels[dayIndex];
      const slot = slots[slotIndex];
      if (!day || !slot) return 'not-available';

      const dayWindow = day.dayWindow;
      if (!dayWindow || slot.startMinutes < dayWindow.startMinutes || slot.endMinutes > dayWindow.endMinutes) {
        return 'not-available';
      }

      const slotStart = new Date(day.dateObj);
      slotStart.setHours(slot.hour, 0, 0, 0);
      const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000);

      const hasOverlap = bookedRanges.some((range) => slotStart < range.end && slotEnd > range.start);
      if (slotStart < now) {
        return hasOverlap ? 'past' : 'not-available';
      }
      return hasOverlap ? 'busy' : 'available';
    };

    return { dayLabels, slots, getSlotStatus, todayKey };
  }, [scheduleMonthStart, property, availability, stayBookedSlots]);

  const scheduleMonthTitle = useMemo(
    () => scheduleMonthStart.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
    [scheduleMonthStart]
  );

  useEffect(() => {
    const activePropertyId = property?._id || property?.id || propertyId;
    if (!activePropertyId) {
      setStayBookedSlots([]);
      return;
    }

    const formatDateParam = (dateValue) => {
      const y = dateValue.getFullYear();
      const m = String(dateValue.getMonth() + 1).padStart(2, '0');
      const d = String(dateValue.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    const monthStart = new Date(scheduleMonthStart.getFullYear(), scheduleMonthStart.getMonth(), 1);
    const nextMonthStart = new Date(scheduleMonthStart.getFullYear(), scheduleMonthStart.getMonth() + 1, 1);
    const fromParam = formatDateParam(monthStart);
    const toParam = formatDateParam(nextMonthStart);

    const controller = new AbortController();

    const fetchBookedSlots = async () => {
      try {
        const response = await fetch(
          getApiUrl(`/api/bookings/stay/property/${activePropertyId}/booked-slots?from=${encodeURIComponent(fromParam)}&to=${encodeURIComponent(toParam)}`),
          { signal: controller.signal }
        );
        const result = await response.json();
        if (result?.success && Array.isArray(result.bookedSlots)) {
          setStayBookedSlots(result.bookedSlots);
          return;
        }
        setStayBookedSlots([]);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.error('Error fetching stay booked slots:', error);
        setStayBookedSlots([]);
      }
    };

    fetchBookedSlots();

    return () => {
      controller.abort();
    };
  }, [property?._id, property?.id, propertyId, scheduleMonthStart]);

  const moveScheduleMonth = useCallback((offset) => {
    setScheduleMonthStart((prev) => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
  }, []);

  const handleAvailabilitySlotSelect = useCallback((day, slot, status) => {
    if (status === 'busy') {
      toast.error('This time slot is already booked. Please select another time.');
      return;
    }
    if (status === 'past' || status === 'not-available') {
      return;
    }

    const selectedCheckIn = new Date(day.dateObj);
    selectedCheckIn.setHours(slot.hour, 0, 0, 0);
    const selectedCheckOut = new Date(selectedCheckIn);
    selectedCheckOut.setDate(selectedCheckOut.getDate() + 1);

    setCheckIn(selectedCheckIn);
    setCheckOut(selectedCheckOut);

    const bookingCard = document.querySelector('.booking-summary-card');
    if (bookingCard) {
      bookingCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  // Calculate discount details
  const discountDetails = useMemo(() => {
    console.log('🔍 Calculating discount for property:', property?.name, 'Discount:', property?.discount);
    
    if (!isDiscountVisibleToGuest(property?.discount) || numberOfNights === 0) {
      console.log('❌ No active discount or no nights selected');
      return {
        hasDiscount: false,
        originalPrice: totalPrice,
        discountAmount: 0,
        discountedPrice: totalPrice,
        discountText: '',
        savingsPercentage: 0
      };
    }

    const discount = property.discount;
    const originalPricePerNight = property.pricePerNight || 0;
    const originalTotalPrice = originalPricePerNight * numberOfNights;
    let discountedPricePerNight = originalPricePerNight;
    let discountText = '';

    console.log('💰 Original price per night:', originalPricePerNight, 'Nights:', numberOfNights);

    if (discount.type === 'percentage') {
      const discountAmount = (originalPricePerNight * discount.value) / 100;
      const maxDiscount = discount.maxDiscountAmount || Infinity;
      const finalDiscountAmount = Math.min(discountAmount, maxDiscount);
      discountedPricePerNight = originalPricePerNight - finalDiscountAmount;
      discountText = `${discount.value}% OFF`;
      console.log('📊 Percentage discount:', discount.value, '% =', finalDiscountAmount, 'per night');
    } else if (discount.type === 'fixed') {
      discountedPricePerNight = Math.max(0, originalPricePerNight - discount.value);
      discountText = `₹${discount.value} OFF`;
      console.log('💵 Fixed discount:', discount.value, 'per night');
    }

    const discountedTotalPrice = discountedPricePerNight * numberOfNights;
    const totalDiscountAmount = originalTotalPrice - discountedTotalPrice;
    const savingsPercentage = Math.round((totalDiscountAmount / originalTotalPrice) * 100);

    const result = {
      hasDiscount: true,
      originalPrice: originalTotalPrice,
      discountAmount: totalDiscountAmount,
      discountedPrice: discountedTotalPrice,
      discountText,
      savingsPercentage,
      discountTitle: discount.title || 'Special Offer',
      discountBadge: discount.badge || 'Special Offer',
      validUntil: discount.validUntil
    };

    console.log('✅ Discount calculation result:', result);
    return result;
  }, [property, numberOfNights, totalPrice]);

  const handleCheckAvailability = useCallback(async () => {
    if (!checkIn || !checkOut) {
      toast.warning('Please select check-in and check-out dates');
      return;
    }

    if (numberOfNights < 1) {
      toast.warning('Check-out date must be after check-in date');
      return;
    }

    setCheckingAvailability(true);

    try {
      // Simulate API call to check availability
      await new Promise(resolve => setTimeout(resolve, 1000));

      // For demo, always show as available
      // Generate room types based on property type
      const roomTypes = {
        'Hotel': ['Standard Double Room', 'Deluxe Room', 'Executive Suite'],
        'Villa': ['Master Bedroom', 'Guest Room', 'Pool View Room'],
        'Homestay': ['Private Room', 'Family Room', 'Shared Room'],
        'PG / Hostel': ['Single Bed', 'Double Bed', 'Triple Sharing'],
        'Resort': ['Garden View Room', 'Sea View Room', 'Premium Villa'],
        'Apartment': ['1 Bedroom Apartment', '2 Bedroom Apartment', 'Studio Apartment'],
        'Guesthouse': ['Standard Room', 'Deluxe Room', 'Family Room'],
        'Other': ['Standard Room', 'Deluxe Room', 'Suite']
      };

      const propertyType = property.type || 'Hotel';
      const availableRoomTypes = roomTypes[propertyType] || roomTypes['Hotel'];
      
      // Create rooms based on property type
      const rooms = availableRoomTypes.slice(0, property.maxGuests >= 4 ? 2 : 1).map((roomName, index) => ({
        id: `room-${index + 1}`,
        name: roomName,
        maxGuests: property.maxGuests || 2,
        bedType: index === 0 ? '1 double bed' : '2 single beds',
        pricePerNight: property.pricePerNight,
        totalPrice: totalPrice,
        cancellationPolicy: 'Free cancellation until 24 hours before check-in',
        amenities: property.amenities || []
      }));

      setAvailability({
        available: true,
        rooms: rooms
      });
    } catch (error) {
      console.error('Error checking availability:', error);
      setAvailability({ available: false });
    } finally {
      setCheckingAvailability(false);
    }
  }, [checkIn, checkOut, numberOfNights, property, totalPrice]);

  // Get property images - only host-uploaded images, no dummy data
  const propertyImages = useMemo(() => {
    if (!property) return [];
    return getPropertyImages(property, []); // Empty array as fallback - no dummy images
  }, [property]);

  const handleImageClick = useCallback((index) => {
    setSelectedImageIndex(index);
    setShowFullscreen(true);
  }, []);

  const handleNextImage = useCallback(() => {
    setSelectedImageIndex((prev) => (prev + 1) % propertyImages.length);
  }, [propertyImages.length]);

  const handlePrevImage = useCallback(() => {
    setSelectedImageIndex((prev) => (prev - 1 + propertyImages.length) % propertyImages.length);
  }, [propertyImages.length]);

  const handleThumbnailClick = useCallback((index) => {
    setSelectedImageIndex(index);
  }, []);

  const handleToggleFavorite = useCallback(() => {
    if (!property) return;

    try {
      // ✅ SECURITY: Get user-specific favorites key
      const getFavoritesKey = () => {
        try {
          const user = localStorage.getItem('user');
          if (user) {
            const userObj = JSON.parse(user);
            return `favorites_${userObj._id || userObj.id}`;
          }
        } catch (error) {
          console.error('Error getting user ID:', error);
        }
        return 'favorites'; // Fallback for non-logged-in users
      };

      // ✅ SECURITY: Get user-specific wishlist count key
      const getWishlistCountKey = () => {
        try {
          const user = localStorage.getItem('user');
          if (user) {
            const userObj = JSON.parse(user);
            return `wishlistCount_${userObj._id || userObj.id}`;
          }
        } catch (error) {
          console.error('Error getting user ID:', error);
        }
        return 'wishlistCount'; // Fallback for non-logged-in users
      };

      const favoritesKey = getFavoritesKey();
      const wishlistCountKey = getWishlistCountKey();
      let favorites = loadWishlistFavorites(favoritesKey);
      const propertyId = property.id || property._id;
      
      // Prepare property data for storage
      const propertyData = {
        id: propertyId,
        _id: propertyId,
        name: property.name,
        city: property.city,
        address: property.address,
        rating: property.rating,
        reviews: property.reviews,
        pricePerNight: property.pricePerNight,
        images: property.images,
        type: property.type,
        ...property
      };

      if (isSaved) {
        // Remove from favorites
        favorites = favorites.filter(fav => getFavoriteId(fav) !== propertyId);
        setIsSaved(false);
      } else {
        // Add to favorites
        favorites = favorites.filter(fav => getFavoriteId(fav) !== propertyId);
        favorites.push({
          ...propertyData,
          wishlistAddedAt: Date.now()
        });
        setIsSaved(true);
      }

      saveWishlistFavorites(favoritesKey, wishlistCountKey, favorites);
      
      // Trigger storage event to update navbar
      window.dispatchEvent(new Event('storage'));
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  }, [property, isSaved]);

  // Auto-refresh availability when dates change (only if already checked)
  useEffect(() => {
    if (checkIn && checkOut && numberOfNights > 0 && availability && !checkingAvailability) {
      // Debounce to avoid too many calls
      const timer = setTimeout(() => {
        handleCheckAvailability();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [checkIn, checkOut, guests, numberOfNights, availability, checkingAvailability, handleCheckAvailability]);

  // Keyboard navigation for fullscreen viewer
  useEffect(() => {
    if (!showFullscreen) return;

    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') {
        handlePrevImage();
      } else if (e.key === 'ArrowRight') {
        handleNextImage();
      } else if (e.key === 'Escape') {
        setShowFullscreen(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showFullscreen, handlePrevImage, handleNextImage]);

  const handleReserve = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      // Redirect to login
      toast.warning('Please login to make a reservation');
      navigate('/login');
      return;
    }

    if (!checkIn || !checkOut) {
      toast.warning('Please select check-in and check-out dates');
      return;
    }

    // Convert dates to ISO strings if they're Date objects.
    // Fall back to property defaults (or 15:00/11:00) when user hasn't
    // explicitly selected a time, so the booking is still well-formed.
    const effectiveCheckInTime = normalizeTimeTo24Hour(checkInTime, normalizeTimeTo24Hour(property?.checkInTime, '15:00'));
    const effectiveCheckOutTime = normalizeTimeTo24Hour(checkOutTime, normalizeTimeTo24Hour(property?.checkOutTime, '11:00'));
    const checkInStr = buildIsoFromDateAndTime(checkIn, effectiveCheckInTime) || (checkIn instanceof Date ? checkIn.toISOString() : checkIn);
    const checkOutStr = buildIsoFromDateAndTime(checkOut, effectiveCheckOutTime) || (checkOut instanceof Date ? checkOut.toISOString() : checkOut);

    // Calculate final pricing with discount
    const basePrice = discountDetails.hasDiscount ? discountDetails.discountedPrice : totalPrice;
    const serviceFeePercent = property.serviceFeePercent ?? 10;
    const serviceFee = Math.round(basePrice * (serviceFeePercent / 100));
    const taxes = Math.round(basePrice * 0.12);
    const finalTotal = basePrice + serviceFee + taxes;

    // Navigate to booking page
    navigate('/booking', {
      state: {
        property,
        checkIn: checkInStr,
        checkOut: checkOutStr,
        checkInTime: effectiveCheckInTime,
        checkOutTime: effectiveCheckOutTime,
        guests,
        guestData,
        numberOfNights,
        pricing: {
          originalPrice: discountDetails.hasDiscount ? discountDetails.originalPrice : totalPrice,
          discountedPrice: basePrice,
          discountAmount: discountDetails.discountAmount,
          serviceFee,
          taxes,
          finalTotal
        },
        discount: discountDetails.hasDiscount ? {
          ...property.discount,
          discountText: discountDetails.discountText,
          savingsAmount: discountDetails.discountAmount,
          savingsPercentage: discountDetails.savingsPercentage
        } : null
      }
    });
  };

  const getAmenityIcon = (amenity) => {
    const amenityLower = amenity.toLowerCase();
    if (amenityLower.includes('wifi')) return <FaWifi />;
    if (amenityLower.includes('parking')) return <FaParking />;
    if (amenityLower.includes('pool')) return <FaSwimmingPool />;
    if (amenityLower.includes('restaurant') || amenityLower.includes('kitchen')) return <FaUtensils />;
    if (amenityLower.includes('gym')) return <FaDumbbell />;
    return <FaCheck />;
  };

  // Get recommended for based on property type and search query
  const getRecommendedFor = useMemo(() => {
    const recommendations = [];
    const searchResults = localStorage.getItem('searchResults');
    let searchQuery = '';
    
    try {
      if (searchResults) {
        const results = JSON.parse(searchResults);
        searchQuery = results.query?.input?.toLowerCase() || '';
      }
    } catch (e) {
      // Ignore
    }

    // Determine recommendations based on property type and search
    if (property?.maxGuests >= 4 || searchQuery.includes('family')) {
      recommendations.push({ type: 'family', icon: <FaUserFriends />, label: 'Family' });
    }
    if (property?.maxGuests >= 2 || searchQuery.includes('couple') || searchQuery.includes('romantic')) {
      recommendations.push({ type: 'couple', icon: <FaCouple />, label: 'Couple' });
    }
    if (searchQuery.includes('business') || property?.type?.toLowerCase().includes('hotel')) {
      recommendations.push({ type: 'business', icon: <FaBriefcase />, label: 'Business' });
    }
    if (property?.maxGuests === 1 || searchQuery.includes('solo') || searchQuery.includes('single')) {
      recommendations.push({ type: 'solo', icon: <FaUser />, label: 'Solo' });
    }

    // Only return recommendations based on actual property data - no dummy data
    return recommendations;
  }, [property]);

  if (loading) {
    return (
      <div className="property-details-page">
        <div className="skeleton-loader">
          {/* Sticky Bar Skeleton */}
          <div className="skeleton-sticky-bar"></div>
          
          {/* Header Skeleton */}
          <div className="skeleton-header">
            <div className="skeleton-title"></div>
            <div className="skeleton-meta">
              <div className="skeleton-badge"></div>
              <div className="skeleton-badge"></div>
            </div>
          </div>

          {/* Images Skeleton */}
          <div className="skeleton-images">
            <div className="skeleton-image-main"></div>
            <div className="skeleton-image-grid">
              <div className="skeleton-image-small"></div>
              <div className="skeleton-image-small"></div>
              <div className="skeleton-image-small"></div>
              <div className="skeleton-image-small"></div>
            </div>
          </div>

          {/* Content Skeleton */}
          <div className="skeleton-content">
            <div className="skeleton-section">
              <div className="skeleton-line long"></div>
              <div className="skeleton-line"></div>
              <div className="skeleton-line medium"></div>
            </div>
            <div className="skeleton-section">
              <div className="skeleton-line short"></div>
              <div className="skeleton-grid">
                <div className="skeleton-item"></div>
                <div className="skeleton-item"></div>
                <div className="skeleton-item"></div>
                <div className="skeleton-item"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="property-not-found">
        <h2>Property Not Found</h2>
        <button onClick={() => navigate(-1)} className="btn-back">
          <FaArrowLeft /> Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="property-details-page">
      {/* Main Content */}
      <div className="property-details-content">
        {/* Breadcrumb Navigation */}
        <nav className="breadcrumb-nav">
          <button onClick={() => navigate('/')} className="breadcrumb-link">
            Home
          </button>
          <FaChevronRightIcon className="breadcrumb-separator" />
          <button onClick={() => navigate(-1)} className="breadcrumb-link">
            Search results
          </button>
          <FaChevronRightIcon className="breadcrumb-separator" />
          <span className="breadcrumb-current">{property.name}</span>
        </nav>

        {/* Property Header */}
        <div className="property-header-section">
          <div className="property-header-left">
            <h1 className="property-title-large">{property.name}</h1>
            <div className="property-rating-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar 
                  key={star} 
                  className={star <= Math.floor(property.rating || 0) ? 'star-filled' : 'star-empty'} 
                />
              ))}
            </div>
            <div className="property-address">
              <FaMapMarkerAlt />
              <span>
                {getSafeString(property.address) 
                  || `${getSafeString(property.city, 'Unknown Location')}${getSafeString(property.state) ? `, ${getSafeString(property.state)}` : ''}`}
              </span>
            </div>
          </div>
          <button 
            className={`save-property-btn ${isSaved ? 'saved' : ''}`}
            onClick={handleToggleFavorite}
          >
            <FaHeart />
            <span>{isSaved ? 'Saved' : 'Save'}</span>
          </button>
        </div>

        {/* Property Images - Only host-uploaded images */}
        {propertyImages.length > 0 ? (
          <div className="property-images-section">
            {/* Desktop Layout: Large image left, 2x2 grid right */}
            <div className="images-grid-desktop">
              {/* Main Large Image */}
              <div 
                className="main-image-large" 
                onClick={() => handleImageClick(0)}
              >
                <img 
                  src={propertyImages[0]} 
                  alt={property.name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                  onLoad={(e) => {
                    e.target.style.opacity = '1';
                  }}
                  style={{ opacity: 0, transition: 'opacity 0.3s ease' }}
                />
                {/* View all photos button overlay - bottom right */}
                {propertyImages.length > 1 && (
                  <button 
                    className="view-all-photos-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleImageClick(0);
                    }}
                    title="View all photos"
                  >
                    <FaExpand />
                    <span>View all photos</span>
                  </button>
                )}
              </div>
              
              {/* 2x2 Grid of Side Images */}
              {propertyImages.length > 1 && (
                <div className="side-images-grid">
                  {propertyImages.slice(1, 5).map((img, index) => (
                    <div 
                      key={index} 
                      className="side-image-item"
                      onClick={() => handleImageClick(index + 1)}
                    >
                      <img 
                        src={img} 
                        alt={`${property.name} ${index + 2}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                        onLoad={(e) => {
                          e.target.style.opacity = '1';
                        }}
                        style={{ opacity: 0, transition: 'opacity 0.3s ease' }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="property-images-section no-images">
            <div className="no-images-placeholder">
              <FaInfoCircle />
              <p>No images available for this property</p>
            </div>
          </div>
        )}

        {/* Mobile Layout: Large image top, horizontal scroll thumbnails */}
        {propertyImages.length > 0 && (
          <div className="images-grid-mobile">
            <div 
              className="main-image-mobile" 
              onClick={() => handleImageClick(0)}
            >
              <img 
                src={propertyImages[0]} 
                alt={property.name}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
                onLoad={(e) => {
                  e.target.style.opacity = '1';
                }}
                style={{ opacity: 0, transition: 'opacity 0.3s ease' }}
              />
              {propertyImages.length > 1 && (
                <button 
                  className="view-all-photos-btn-mobile"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImageClick(0);
                  }}
                  title="View all photos"
                >
                  <FaExpand />
                  <span>View all photos</span>
                </button>
              )}
            </div>
            
            {/* Horizontal Scroll Thumbnails */}
            {propertyImages.length > 1 && (
              <div className="thumbnail-scroll-container">
                {propertyImages.map((img, index) => (
                  <div
                    key={index}
                    className={`thumbnail-scroll-item ${selectedImageIndex === index ? 'active' : ''}`}
                    onClick={() => {
                      handleThumbnailClick(index);
                      handleImageClick(index);
                    }}
                  >
                    <img 
                      src={img} 
                      alt={`${property.name} ${index + 1}`}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Fullscreen Image Viewer */}
        {showFullscreen && propertyImages.length > 0 && (
          <div className="fullscreen-viewer" onClick={() => setShowFullscreen(false)}>
            <button 
              className="close-fullscreen"
              onClick={(e) => {
                e.stopPropagation();
                setShowFullscreen(false);
              }}
            >
              <FaTimes />
            </button>
            {propertyImages.length > 1 && (
              <button 
                className="nav-image prev"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevImage();
                }}
              >
                <FaChevronLeft />
              </button>
            )}
            <div className="fullscreen-image-container" onClick={(e) => e.stopPropagation()}>
              <img 
                src={propertyImages[selectedImageIndex]} 
                alt={`${property.name} ${selectedImageIndex + 1}`}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              {propertyImages.length > 1 && (
                <div className="image-counter">
                  {selectedImageIndex + 1} / {propertyImages.length}
                </div>
              )}
            </div>
            {propertyImages.length > 1 && (
              <button 
                className="nav-image next"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNextImage();
                }}
              >
                <FaChevronRight />
              </button>
            )}
          </div>
        )}

        {/* Navigation Tabs: horizontal slider on mobile (scroll), tabs on desktop */}
        <div className="property-tabs-wrapper">
          <div className="property-tabs">
            <button 
              className={`property-tab ${activeTab === 'description' ? 'active' : ''}`}
              onClick={() => setActiveTab('description')}
            >
              Description
            </button>
            <button 
              className={`property-tab ${activeTab === 'services' ? 'active' : ''}`}
              onClick={() => setActiveTab('services')}
            >
              Services
            </button>
            <button 
              className={`property-tab ${activeTab === 'surroundings' ? 'active' : ''}`}
              onClick={() => setActiveTab('surroundings')}
            >
              Surroundings
            </button>
            <button 
              className={`property-tab ${activeTab === 'comments' ? 'active' : ''}`}
              onClick={() => setActiveTab('comments')}
            >
              Comments
            </button>
            <button 
              className={`property-tab ${activeTab === 'rules' ? 'active' : ''}`}
              onClick={() => setActiveTab('rules')}
            >
              Rules
            </button>
            <button 
              className={`property-tab ${activeTab === 'policy' ? 'active' : ''}`}
              onClick={() => setActiveTab('policy')}
            >
              Policy
            </button>
            <button
              className={`property-tab ${activeTab === 'schedule' ? 'active' : ''}`}
              onClick={() => setActiveTab('schedule')}
            >
              Availability
            </button>
          </div>
        </div>

        <div className={`property-main-layout ${activeTab === 'schedule' ? 'availability-layout' : ''}`}>
          {/* Left Column - Details */}
          <div className="property-left-column">
            {/* Description Tab */}
            {activeTab === 'description' && (
              <section className="property-section">
                <h2>Description</h2>
                <div className="property-description-detailed">
                  <p className="property-description-main">
                    {getSafeString(property.description) || `Welcome to ${getSafeString(property.name)}, a beautiful ${getSafeString(property.type, 'property')} located in ${getSafeString(property.city, 'the city')}. This property offers comfortable accommodation with modern amenities.`}
                  </p>
                  
                  {/* Property Highlights */}
                  {(property.bedrooms || property.bathrooms || property.maxGuests) && (
                    <div className="property-highlights">
                      <h3>Property Highlights</h3>
                      <ul className="highlights-list">
                        {property.city && <li>Located in {getSafeString(property.city, 'the city')}</li>}
                        {property.bedrooms && <li>{property.bedrooms} {property.bedrooms === 1 ? 'bedroom' : 'bedrooms'} with modern furnishings</li>}
                        {property.bathrooms && <li>{property.bathrooms} {property.bathrooms === 1 ? 'bathroom' : 'bathrooms'} with premium fixtures</li>}
                        {property.maxGuests && <li>Accommodates up to {property.maxGuests} {property.maxGuests === 1 ? 'guest' : 'guests'}</li>}
                        {property.amenities && property.amenities.length > 0 && <li>Fully equipped with modern amenities</li>}
                      </ul>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Services Tab */}
            {activeTab === 'services' && (
              <section className="property-section">
                <h2>Facilities</h2>
                
                {property.amenities && property.amenities.length > 0 ? (
                  <>
                    {/* Most Popular Facilities Summary */}
                    <div className="facilities-summary">
                      <span className="facility-badge">
                        <FaWifi /> Free WiFi
                      </span>
                      {property.amenities.some(a => a.toLowerCase().includes('parking')) && (
                        <span className="facility-badge">
                          <FaParking /> Free parking
                        </span>
                      )}
                      {property.amenities.some(a => a.toLowerCase().includes('pool')) && (
                        <span className="facility-badge">
                          <FaSwimmingPool /> Swimming pool
                        </span>
                      )}
                      {property.amenities.some(a => a.toLowerCase().includes('restaurant')) && (
                        <span className="facility-badge">
                          <FaUtensils /> Restaurant
                        </span>
                      )}
                      {property.amenities.some(a => a.toLowerCase().includes('gym')) && (
                        <span className="facility-badge">
                          <FaDumbbell /> Fitness center
                        </span>
                      )}
                      <span className="facility-badge">
                        <FaTv /> Flat-screen TV
                      </span>
                    </div>

                    {/* Enhanced Facilities Section */}
                    <div className="facilities-enhanced-section">
                      <div className="facilities-intro">
                        <p>
                          {getSafeString(property.name, 'This property')} offers excellent facilities to ensure a comfortable and enjoyable stay.
                          All rooms are equipped with modern amenities for your convenience.
                        </p>
                      </div>

                      <div className="facilities-grid-container">
                        {/* Room Features */}
                        <div className="facility-category-enhanced">
                          <h3 className="facility-category-title">
                            <FaBed /> Room Features
                          </h3>
                          <div className="facility-items-list">
                            <div className="facility-item-enhanced">
                              <FaSnowflake />
                              <span>Air conditioning</span>
                            </div>
                            <div className="facility-item-enhanced">
                              <FaTv />
                              <span>Flat-screen TV</span>
                            </div>
                            <div className="facility-item-enhanced">
                              <FaCouch />
                              <span>Comfortable seating area</span>
                            </div>
                            <div className="facility-item-enhanced">
                              <FaDoorOpen />
                              <span>Private entrance</span>
                            </div>
                            <div className="facility-item-enhanced">
                              <FaFan />
                              <span>Ceiling fan</span>
                            </div>
                          </div>
                        </div>

                        {/* Bathroom */}
                        <div className="facility-category-enhanced">
                          <h3 className="facility-category-title">
                            <FaBath /> Bathroom
                          </h3>
                          <div className="facility-items-list">
                            <div className="facility-item-enhanced">
                              <FaShower />
                              <span>Private bathroom</span>
                            </div>
                            <div className="facility-item-enhanced">
                              <FaSoap />
                              <span>Free toiletries</span>
                            </div>
                            <div className="facility-item-enhanced">
                              <FaBath />
                              <span>Bathtub or shower</span>
                            </div>
                            <div className="facility-item-enhanced">
                              <FaWind />
                              <span>Hair dryer</span>
                            </div>
                          </div>
                        </div>

                        {/* Internet */}
                        <div className="facility-category-enhanced">
                          <h3 className="facility-category-title">
                            <FaWifi /> Internet
                          </h3>
                          <div className="facility-items-list">
                            <div className="facility-item-enhanced">
                              <FaWifi />
                              <span>Free WiFi available in all areas</span>
                            </div>
                            <div className="facility-item-enhanced">
                              <FaWifi />
                              <span>High-speed internet connection</span>
                            </div>
                          </div>
                        </div>

                        {/* Parking */}
                        <div className="facility-category-enhanced">
                          <h3 className="facility-category-title">
                            <FaParking /> Parking
                          </h3>
                          <div className="facility-items-list">
                            {property.amenities.some(a => a.toLowerCase().includes('parking')) ? (
                              <>
                                <div className="facility-item-enhanced">
                                  <FaParking />
                                  <span>Free private parking on-site</span>
                                </div>
                                <div className="facility-item-enhanced">
                                  <FaCheck />
                                  <span>No reservation needed</span>
                                </div>
                              </>
                            ) : (
                              <div className="facility-item-enhanced">
                                <FaParking />
                                <span>Parking available nearby</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Food & Drink */}
                        <div className="facility-category-enhanced">
                          <h3 className="facility-category-title">
                            <FaUtensils /> Food & Drink
                          </h3>
                          <div className="facility-items-list">
                            {property.amenities.some(a => a.toLowerCase().includes('restaurant')) ? (
                              <>
                                <div className="facility-item-enhanced">
                                  <FaUtensils />
                                  <span>Restaurant on-site</span>
                                </div>
                                <div className="facility-item-enhanced">
                                  <FaConciergeBell />
                                  <span>Room service available</span>
                                </div>
                                <div className="facility-item-enhanced">
                                  <FaCoffee />
                                  <span>Coffee/tea maker</span>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="facility-item-enhanced">
                                  <FaUtensils />
                                  <span>Restaurant nearby</span>
                                </div>
                                <div className="facility-item-enhanced">
                                  <FaCoffee />
                                  <span>Coffee/tea maker</span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Recreation & Wellness */}
                        {(property.amenities.some(a => a.toLowerCase().includes('pool') || a.toLowerCase().includes('gym'))) && (
                          <div className="facility-category-enhanced">
                            <h3 className="facility-category-title">
                              <FaSwimmingPool /> Recreation & Wellness
                            </h3>
                            <div className="facility-items-list">
                              {property.amenities.some(a => a.toLowerCase().includes('pool')) && (
                                <div className="facility-item-enhanced">
                                  <FaSwimmingPool />
                                  <span>Outdoor swimming pool</span>
                                </div>
                              )}
                              {property.amenities.some(a => a.toLowerCase().includes('gym')) && (
                                <div className="facility-item-enhanced">
                                  <FaDumbbell />
                                  <span>Fitness center</span>
                                </div>
                              )}
                              <div className="facility-item-enhanced">
                                <FaTree />
                                <span>Garden area</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* General */}
                        <div className="facility-category-enhanced">
                          <h3 className="facility-category-title">
                            <FaCheck /> General
                          </h3>
                          <div className="facility-items-list">
                            <div className="facility-item-enhanced">
                              <FaSmokingBan />
                              <span>Non-smoking rooms</span>
                            </div>
                            <div className="facility-item-enhanced">
                              <FaConciergeBell />
                              <span>24-hour front desk</span>
                            </div>
                            <div className="facility-item-enhanced">
                              <FaCheck />
                              <span>Daily housekeeping</span>
                            </div>
                            <div className="facility-item-enhanced">
                              <FaCheck />
                              <span>Luggage storage</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* CTA Section */}
                      <div className="facilities-cta-section">
                        <h3 className="facilities-cta-title">Ready to book your stay?</h3>
                        <p className="facilities-cta-subtitle">
                          Check availability and secure the best rates for your dates
                        </p>
                        <button 
                          className="btn-see-availability"
                          onClick={() => {
                            // Scroll to booking card
                            const bookingCard = document.querySelector('.booking-summary-card');
                            if (bookingCard) {
                              bookingCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                          }}
                        >
                          <FaCalendarAlt /> See availability
                        </button>
                      </div>
                    </div>

                    <div className="amenities-list" style={{ display: 'none' }}>
                      {property.amenities.map((amenity, index) => (
                        <div key={index} className="amenity-item">
                          {getAmenityIcon(amenity)}
                          <span>{amenity}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* All Facilities Section - Detailed List Format */}
                    <div className="all-facilities-section">
                      <h3>All Facilities</h3>
                      <div className="facilities-detailed-list">
                        {/* Internet */}
                        <div className="facility-category">
                          <h4>Internet</h4>
                          <ul className="facility-category-list">
                            <li>
                              <FaWifi />
                              <span>Free WiFi available in all areas</span>
                            </li>
                            <li>
                              <FaWifi />
                              <span>High-speed internet connection</span>
                            </li>
                          </ul>
                        </div>

                        {/* Parking */}
                        <div className="facility-category">
                          <h4>Parking</h4>
                          <ul className="facility-category-list">
                            {property.amenities.some(a => a.toLowerCase().includes('parking')) ? (
                              <li>
                                <FaParking />
                                <span>Free private parking available on-site</span>
                              </li>
                            ) : (
                              <li>
                                <FaParking />
                                <span>Parking available nearby (charges may apply)</span>
                              </li>
                            )}
                          </ul>
                        </div>

                        {/* Food & Drink */}
                        <div className="facility-category">
                          <h4>Food & Drink</h4>
                          <ul className="facility-category-list">
                            {property.amenities.some(a => a.toLowerCase().includes('restaurant')) ? (
                              <>
                                <li>
                                  <FaUtensils />
                                  <span>Restaurant on-site</span>
                                </li>
                                <li>
                                  <FaUtensils />
                                  <span>Room service available</span>
                                </li>
                              </>
                            ) : (
                              <li>
                                <FaUtensils />
                                <span>Restaurant nearby</span>
                              </li>
                            )}
                          </ul>
                        </div>

                        {/* Recreation */}
                        <div className="facility-category">
                          <h4>Recreation</h4>
                          <ul className="facility-category-list">
                            {property.amenities.some(a => a.toLowerCase().includes('pool')) && (
                              <li>
                                <FaSwimmingPool />
                                <span>Swimming pool</span>
                              </li>
                            )}
                            {property.amenities.some(a => a.toLowerCase().includes('gym')) && (
                              <li>
                                <FaDumbbell />
                                <span>Fitness center</span>
                              </li>
                            )}
                            {!property.amenities.some(a => a.toLowerCase().includes('pool') || a.toLowerCase().includes('gym')) && (
                              <li>
                                <FaInfoCircle />
                                <span>Recreation facilities nearby</span>
                              </li>
                            )}
                          </ul>
                        </div>

                        {/* Additional Facilities from Property */}
                        {property.amenities.filter(a => 
                          !a.toLowerCase().includes('wifi') && 
                          !a.toLowerCase().includes('parking') && 
                          !a.toLowerCase().includes('restaurant') && 
                          !a.toLowerCase().includes('pool') && 
                          !a.toLowerCase().includes('gym')
                        ).length > 0 && (
                          <div className="facility-category">
                            <h4>Additional Facilities</h4>
                            <ul className="facility-category-list">
                              {property.amenities.filter(a => 
                                !a.toLowerCase().includes('wifi') && 
                                !a.toLowerCase().includes('parking') && 
                                !a.toLowerCase().includes('restaurant') && 
                                !a.toLowerCase().includes('pool') && 
                                !a.toLowerCase().includes('gym')
                              ).map((amenity, index) => (
                                <li key={index}>
                                  {getAmenityIcon(amenity)}
                                  <span>{amenity}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <p>No facilities listed for this property.</p>
                )}
              </section>
            )}

            {/* Comments Tab - Guest Reviews */}
            {activeTab === 'comments' && (
              <section className="property-section">
                <h2>Guest Reviews</h2>

                {loadingReviews ? (
                  <div className="reviews-loading">
                    <FaSpinner className="spinner" />
                    <span>Loading reviews...</span>
                  </div>
                ) : (
                  <>
                    <div className="reviews-summary-bar">
                      <span className="reviews-count-text">
                        {propertyReviews.length === 0
                          ? 'No reviews yet'
                          : `${propertyReviews.length} review${propertyReviews.length !== 1 ? 's' : ''}`}
                      </span>
                      {propertyReviews.length > 0 && (
                        <span className="reviews-avg-rating">
                          <FaStar className="star-filled" />
                          {(propertyReviews.reduce((s, r) => s + r.rating, 0) / propertyReviews.length).toFixed(1)} average
                        </span>
                      )}
                    </div>

                    {propertyReviews.length > 0 && (
                      <div className="reviews-list">
                        {propertyReviews.map((rev) => (
                          <div key={rev._id} className="review-card">
                            <div className="review-header">
                              <span className="review-guest-name">{rev.guestName || 'Guest'}</span>
                              <div className="review-stars">
                                {[1, 2, 3, 4, 5].map((s) => (
                                  <FaStar key={s} className={s <= (rev.rating || 0) ? 'star-filled' : 'star-empty'} />
                                ))}
                              </div>
                              <span className="review-date">
                                {rev.createdAt ? new Date(rev.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                              </span>
                            </div>
                            {rev.comment && <p className="review-comment">{rev.comment}</p>}
                            {rev.hostReply && (
                              <div className="review-host-reply">
                                <strong>Host's reply:</strong>
                                <p>{rev.hostReply}</p>
                                {rev.hostRepliedAt && (
                                  <span className="host-reply-date">
                                    {new Date(rev.hostRepliedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="write-review-section">
                      <h3>Leave your feedback</h3>
                      {!currentUser ? (
                        <p className="review-login-prompt">Please log in to leave a review.</p>
                      ) : reviewSubmitted ? (
                        <div className="review-success">
                          <FaCheck />
                          <span>Thank you! Your review has been posted.</span>
                        </div>
                      ) : (
                        <>
                          <div className="review-rating-input">
                            <span className="rating-label">Rating:</span>
                            <div className="rating-stars-input">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <button
                                  key={s}
                                  type="button"
                                  className={`star-btn ${s <= reviewRating ? 'selected' : ''}`}
                                  onClick={() => setReviewRating(s)}
                                  aria-label={`${s} star${s > 1 ? 's' : ''}`}
                                >
                                  <FaStar />
                                </button>
                              ))}
                            </div>
                          </div>
                          <textarea
                            className="review-comment-input"
                            placeholder="Share your experience (optional)"
                            value={reviewComment}
                            onChange={(e) => setReviewComment(e.target.value)}
                            rows={4}
                            maxLength={2000}
                          />
                          <button
                            type="button"
                            className="btn-submit-review"
                            onClick={handleSubmitReview}
                            disabled={reviewRating < 1 || reviewSubmitting}
                          >
                            {reviewSubmitting ? (
                              <><FaSpinner className="spinner" /> Submitting...</>
                            ) : (
                              'Submit Review'
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}

                {/* Similar Accommodations Section - Same Host & Similar Location */}
                <div className="similar-accommodations-section">
                  <h2 className="similar-accommodations-title">
                    <span className="home-icon">🏠</span> View more accommodations
                  </h2>
                  <p className="similar-accommodations-subtitle">
                    Other stays from the same host or in similar locations
                  </p>
                  {loadingSimilar ? (
                    <div className="loading-similar">
                      <FaSpinner className="spinner" />
                      <span>Loading similar accommodations...</span>
                    </div>
                  ) : similarAccommodations.length > 0 ? (
                    <div className="accommodations-table-container">
                      <table className="accommodations-table">
                        <thead>
                          <tr>
                            <th>ACCOMMODATION NAME</th>
                            <th>GUESTS CAPACITY</th>
                            <th>PRICE PER NIGHT</th>
                            <th>ROOM TYPE</th>
                            <th>ACTION</th>
                          </tr>
                        </thead>
                        <tbody>
                          {similarAccommodations.map((acc) => (
                            <tr key={acc._id || acc.id} className="accommodation-row">
                              <td className="accommodation-info">
                                <div className="accommodation-name-location">
                                  <h4 className="accommodation-name">{acc.name}</h4>
                                  <div className="accommodation-location-text">
                                    {acc.city || 'N/A'}
                                  </div>
                                </div>
                              </td>
                              <td className="accommodation-guests">
                                <FaUsers className="guests-icon" />
                                <span>{acc.maxGuests || 2} guests</span>
                              </td>
                              <td className="accommodation-price">
                                <div className="price-display">
                                  <span className="price-amount">₹{acc.pricePerNight?.toLocaleString() || '0'}</span>
                                </div>
                              </td>
                              <td className="accommodation-type">
                                <span>{acc.type || 'Villa'}</span>
                              </td>
                              <td className="accommodation-action">
                                <div className="action-buttons">
                                  <button
                                    className="btn-view-accommodation"
                                    onClick={() => {
                                      const propId = acc._id || acc.id;
                                      let cleanId = propId?.toString();
                                      if (cleanId && cleanId.startsWith('host-')) {
                                        cleanId = cleanId.replace(/^host-/, '');
                                      }
                                      if (!cleanId) return;
                                      navigate(`/property/${cleanId}`, { 
                                        state: { scrollToTop: true },
                                        replace: false 
                                      });
                                      setTimeout(() => {
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                      }, 100);
                                    }}
                                  >
                                    View details
                                  </button>
                                  <button
                                    className="btn-book-accommodation"
                                    onClick={() => {
                                      const propId = acc._id || acc.id;
                                      let cleanId = propId?.toString();
                                      if (cleanId && cleanId.startsWith('host-')) {
                                        cleanId = cleanId.replace(/^host-/, '');
                                      }
                                      navigate(`/property/${cleanId}`, { 
                                        state: { scrollToTop: true },
                                        replace: false 
                                      });
                                      setTimeout(() => {
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                      }, 100);
                                    }}
                                  >
                                    Book
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="no-similar-accommodations">
                      <p>No similar accommodations found at this time. Check back later for more options!</p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Rules Tab - only host-posted rules */}
            {activeTab === 'rules' && (
              <section className="property-section rules-tab-section">
                <h2>House Rules</h2>
                {property.houseRules && property.houseRules.length > 0 ? (
                  <div className="house-rules-section">
                    <h3 className="rules-heading">
                      <span className="rules-symbol" aria-hidden="true">▲</span>
                      Important Guidelines
                    </h3>
                    <ul className="house-rules-list">
                      {property.houseRules.map((rule, index) => (
                        <li key={index} className="rule-item">
                          <span className="rule-symbol" aria-hidden="true">▲</span>
                          <strong className="rule-text">{rule}</strong>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="no-rules-message">
                    <p>No house rules listed for this property. Please contact the host for more information.</p>
                  </div>
                )}
              </section>
            )}

            {/* Policy Tab - only host-added policy content */}
            {activeTab === 'policy' && (
              <section className="property-section policy-tab-section">
                <h2>Property Policies</h2>
                {((property.policies?.cancellationPolicy && property.policies.cancellationPolicy.trim()) ||
                  (property.policies?.paymentPolicy && property.policies.paymentPolicy.trim()) ||
                  (property.policies?.additionalNotes && property.policies.additionalNotes.trim())) ? (
                  <>
                    {property.policies?.cancellationPolicy?.trim() && (
                      <div className="policy-section">
                        <h3>Cancellation Policy</h3>
                        <div className="policy-card">
                          <div className="policy-icon-wrap">
                            <FaCheck className="policy-icon" />
                          </div>
                          <div className="policy-card-content">
                            <strong className="policy-card-title">{property.policies.cancellationPolicy.trim()}</strong>
                            <p className="policy-card-desc">Please review the cancellation terms before booking.</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {property.policies?.paymentPolicy?.trim() && (
                      <div className="policy-section">
                        <h3>Payment Policy</h3>
                        <div className="policy-card">
                          <div className="policy-icon-wrap">
                            <FaRupeeSign className="policy-icon" />
                          </div>
                          <div className="policy-card-content">
                            <strong className="policy-card-title">{property.policies.paymentPolicy.trim()}</strong>
                            <p className="policy-card-desc">Payment method and timing as per the selected policy.</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {property.policies?.additionalNotes?.trim() && (
                      <div className="policy-section">
                        <h3>Additional Policy Information</h3>
                        <div className="policy-card">
                          <div className="policy-icon-wrap">
                            <FaInfoCircle className="policy-icon" />
                          </div>
                          <div className="policy-card-content">
                            <p className="policy-card-desc policy-card-notes">{property.policies.additionalNotes.trim()}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-policy-message">
                    <p>No policy information listed for this property. Please contact the host for details.</p>
                  </div>
                )}
              </section>
            )}

            {activeTab === 'schedule' && (
              <section className="property-section schedule-tab-section">
                <div className="availability-board-header">
                  <div>
                    <p className="availability-board-eyebrow">Monthly Board</p>
                    <h2>Room Availability</h2>
                    <p className="availability-board-subtitle">
                      {checkIn
                        ? 'Full-month slots are shown for your selected month. Click any available slot to set your stay dates.'
                        : 'Select an available slot from this monthly board to auto-fill booking dates.'}
                    </p>
                  </div>
                  <div className="availability-board-month-controls">
                    <button type="button" onClick={() => moveScheduleMonth(-1)} aria-label="Previous month">
                      <FaChevronLeft />
                    </button>
                    <span>{scheduleMonthTitle}</span>
                    <button type="button" onClick={() => moveScheduleMonth(1)} aria-label="Next month">
                      <FaChevronRight />
                    </button>
                  </div>
                  <div className="availability-board-legend">
                    <span><i className="legend-dot available" />Available</span>
                    <span><i className="legend-dot busy" />Booked</span>
                    <span><i className="legend-dot past" />Past</span>
                    <span><i className="legend-dot not-available" />Not available</span>
                  </div>
                </div>

                <div className="availability-board-scroll">
                  <table className="availability-board-table">
                    <thead>
                      <tr>
                        <th className="availability-time-head">
                          <FaCalendarAlt />
                          <span>Time</span>
                        </th>
                        {availabilityBoard.dayLabels.map((day) => (
                          <th key={day.key} className={day.isToday ? 'current-day-head' : ''}>
                            <span className="day-label">{day.dayLabel}</span>
                            <span className="day-date">{day.dateLabel}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {availabilityBoard.slots.map((slot, slotIndex) => (
                        <tr key={slot.key}>
                          <td className="availability-time-cell">{slot.label}</td>
                          {availabilityBoard.dayLabels.map((day, dayIndex) => {
                            const status = availabilityBoard.getSlotStatus(slotIndex, dayIndex);
                            const statusLabel = status === 'available'
                              ? 'Available'
                              : status === 'busy'
                                ? 'Booked'
                                : status === 'past'
                                  ? 'Past'
                                : 'Not available';

                            return (
                              <td key={`${day.key}-${slot.key}`} className={`availability-slot-cell ${status}`}>
                                <button
                                  type="button"
                                  className={`availability-slot ${status}`}
                                  disabled={status !== 'available'}
                                  onClick={() => handleAvailabilitySlotSelect(day, slot, status)}
                                >
                                  {statusLabel}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Recommended For Section - Only show if there are recommendations */}
            {getRecommendedFor.length > 0 && (
              <section className="property-section recommended-for-section">
                <h2>Recommended for</h2>
                <div className="recommended-for-grid">
                  {getRecommendedFor.map((rec, index) => (
                    <div key={index} className="recommended-item">
                      <div className="recommended-icon">{rec.icon}</div>
                      <span className="recommended-label">{rec.label}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* About This Property Section */}
            {(property.amenities?.length > 0 || property.city || property.type) && (
              <section className="property-section about-property-section">
                <h2>About this property</h2>
                <div className="about-property-content">
                  {property.amenities && property.amenities.length > 0 && (
                    <div className="about-facilities">
                      <h3>Facilities</h3>
                      <p>
                        {property.name} offers a range of modern facilities to ensure a comfortable stay. 
                        The property features {property.amenities.slice(0, 3).join(', ')}
                        {property.amenities.length > 3 && ' and more'}.
                      </p>
                    </div>
                  )}
                  {property.city && (
                    <div className="about-surroundings">
                      <h3>Location</h3>
                      <p>
                        Located in {getSafeString(property.city, 'the city')}, this property offers convenient access to local amenities and attractions.
                      </p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Property Info Table - only actual property data */}
            <section className="property-section property-info-table-section">
              <h2>Property Information</h2>
              <div className="info-table">
                {property.bedrooms != null && property.bedrooms > 0 && (
                  <div className="info-table-row">
                    <div className="info-table-label">
                      <FaBed />
                      <span>Bedrooms</span>
                    </div>
                    <div className="info-table-value">{property.bedrooms} {property.bedrooms === 1 ? 'bedroom' : 'bedrooms'}</div>
                  </div>
                )}
                {property.bathrooms != null && property.bathrooms > 0 && (
                  <div className="info-table-row">
                    <div className="info-table-label">
                      <FaBath />
                      <span>Bathrooms</span>
                    </div>
                    <div className="info-table-value">{property.bathrooms} {property.bathrooms === 1 ? 'bathroom' : 'bathrooms'}</div>
                  </div>
                )}
                {property.maxGuests != null && property.maxGuests > 0 && (
                  <div className="info-table-row">
                    <div className="info-table-label">
                      <FaUsers />
                      <span>Guests</span>
                    </div>
                    <div className="info-table-value">Up to {property.maxGuests} {property.maxGuests === 1 ? 'guest' : 'guests'}</div>
                  </div>
                )}
                {property.type && (
                  <div className="info-table-row">
                    <div className="info-table-label">
                      <FaHome />
                      <span>Property type</span>
                    </div>
                    <div className="info-table-value">{property.type}</div>
                  </div>
                )}
                {property.amenities?.some(a => a.toLowerCase().includes('restaurant')) && (
                  <div className="info-table-row">
                    <div className="info-table-label">
                      <FaUtensils />
                      <span>Food & Dining</span>
                    </div>
                    <div className="info-table-value">Restaurant available</div>
                  </div>
                )}
                {property.amenities?.some(a => a.toLowerCase().includes('parking')) && (
                  <div className="info-table-row">
                    <div className="info-table-label">
                      <FaParking />
                      <span>Parking</span>
                    </div>
                    <div className="info-table-value">Free parking available</div>
                  </div>
                )}
                {property.amenities?.some(a => a.toLowerCase().includes('wifi')) && (
                  <div className="info-table-row">
                    <div className="info-table-label">
                      <FaWifi />
                      <span>Wi-Fi</span>
                    </div>
                    <div className="info-table-value">Free Wi-Fi available</div>
                  </div>
                )}
              </div>
              {(!property.bedrooms || property.bedrooms === 0) &&
               (!property.bathrooms || property.bathrooms === 0) &&
               (!property.maxGuests || property.maxGuests === 0) &&
               !property.type &&
               !property.amenities?.some(a => /restaurant|parking|wifi/i.test(a)) && (
                <p className="info-table-empty">No additional property information available for this listing.</p>
              )}
            </section>

            {/* Surroundings Tab */}
            {activeTab === 'surroundings' && (
              <section className="property-section surroundings-section">
                <h2>Surroundings</h2>
                
                {/* Nearby Places - Manual Entry */}
                {property.nearbyPlaces && property.nearbyPlaces.length > 0 && (
                  <div className="nearby-places-section">
                    <h3>Nearby Places</h3>
                    <div className="surroundings-list">
                    {property.nearbyPlaces.map((place, index) => {
                      // Get icon based on category
                      const getPlaceIcon = (category) => {
                        const cat = category?.toLowerCase() || '';
                        if (cat.includes('bus')) return <FaBus />;
                        if (cat.includes('train') || cat.includes('railway')) return <FaTrain />;
                        if (cat.includes('mall') || cat.includes('shopping')) return <FaShoppingBag />;
                        if (cat.includes('tourist') || cat.includes('landmark')) return <FaLandmark />;
                        return <FaMapMarkerAlt />;
                      };

                      return (
                        <div key={index} className="surrounding-item">
                          {getPlaceIcon(place.category)}
                          <div className="surrounding-details">
                            <strong>{place.name}</strong>
                            <span>{place.distance} {place.unit} away</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tourist Places - With Images */}
              {property.touristPlaces && property.touristPlaces.length > 0 && (
                <div className="tourist-attractions-section">
                  <h3>Tourist Attractions Nearby</h3>
                  <div className="tourist-attractions-grid">
                    {property.touristPlaces.map((place, index) => {
                      const touristFallback = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=600&q=80';
                      const imageUrl =
                        place.image && String(place.image).trim()
                          ? getImageUrl(place.image)
                          : touristFallback;

                      return (
                        <div key={index} className="tourist-attraction-card">
                          <div className="tourist-attraction-image">
                            <img 
                              src={imageUrl} 
                              alt={place.name || 'Tourist attraction'}
                              loading="lazy"
                              onError={(e) => {
                                e.target.src = touristFallback;
                              }}
                            />
                            <div className="tourist-distance-badge">
                              {place.distance} km
                            </div>
                          </div>
                          <div className="tourist-attraction-info">
                            <h4>{place.name}</h4>
                            {place.description && <p>{place.description}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Show message if no surroundings data */}
              {(!property.nearbyPlaces || property.nearbyPlaces.length === 0) && 
               (!property.touristPlaces || property.touristPlaces.length === 0) && (
                <div className="no-surroundings-message">
                  <p>No nearby places or tourist attractions information available for this property.</p>
                </div>
              )}
              </section>
            )}

            {/* Q&A Section - actual host-answered questions + Ask a question */}
            <section className="property-section qa-section">
              <h2>Questions & Answers</h2>
              <div className="qa-posted-by">
                <span className="qa-posted-by-label">Posted by:</span>
                <span className="qa-posted-by-name">{qaOwnerInfo.name}</span>
                <span className="qa-posted-by-email">Email: {qaOwnerInfo.email}</span>
              </div>
              <div className="qa-container">
                {/* Left Side - Actual Q&A from API (host-answered only) */}
                <div className="qa-left">
                  {loadingQA ? (
                    <div className="qa-loading">
                      <FaSpinner className="spinner" /> Loading Q&A...
                    </div>
                  ) : propertyQA.length === 0 ? (
                    <p className="qa-empty">No questions answered yet. Be the first to ask!</p>
                  ) : (
                    <>
                      <div className="qa-list">
                        {(showAllQuestions ? propertyQA : propertyQA.slice(0, 2)).map((qa) => (
                          <div key={qa._id} className="qa-item">
                            <div className="qa-question">
                              <FaQuestionCircle />
                              <strong>{qa.question}</strong>
                            </div>
                            <div className="qa-answer">{qa.answer}</div>
                          </div>
                        ))}
                      </div>
                      {propertyQA.length > 2 && (
                        <button
                          className="btn-toggle-qa"
                          onClick={() => setShowAllQuestions(!showAllQuestions)}
                        >
                          {showAllQuestions ? (
                            <>View less <FaChevronLeft style={{ transform: 'rotate(90deg)' }} /></>
                          ) : (
                            <>View more <FaChevronRight style={{ transform: 'rotate(90deg)' }} /></>
                          )}
                        </button>
                      )}
                    </>
                  )}
                </div>

                {/* Right Side - Ask a question about this property */}
                <div className="qa-right">
                  <div className="ask-question-box">
                    <h3 className="ask-question-title">Ask a question about this property</h3>
                    <p className="ask-question-subtitle">
                      Get quick answers from property staff and past guests
                    </p>
                    {!questionSubmitted ? (
                      <div className="ask-question-form">
                        <textarea
                          className="question-textarea"
                          placeholder="What would you like to know about this property?"
                          value={question}
                          onChange={(e) => {
                            if (e.target.value.length <= 500) setQuestion(e.target.value);
                          }}
                          maxLength={500}
                          rows={4}
                        />
                        <div className="question-meta">
                          <span className="character-count">{question.length}/500 characters</span>
                          {!currentUser && (
                            <span className="login-note">Please sign in to ask questions</span>
                          )}
                        </div>
                        <button
                          className="btn-submit-question"
                          onClick={async () => {
                            if (!currentUser) {
                              toast.warning('Please sign in to ask a question');
                              return;
                            }
                            if (!question.trim()) return;
                            setQuestionSubmitting(true);
                            try {
                              const token = localStorage.getItem('token');
                              const url = getQaUrl(`/api/properties/${propertyId}/qa`);
                              const res = await fetch(url, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                                },
                                body: JSON.stringify({ question: question.trim() }),
                              });
                              const contentType = res.headers.get('content-type');
                              const isJson = contentType && contentType.includes('application/json');
                              if (!isJson) {
                                const text = await res.text();
                                console.error('Submit question: non-JSON response', res.status, text?.slice(0, 200));
                                toast.error(`Request failed (${res.status}). Ensure the API server is running and REACT_APP_API_URL points to it (e.g. http://localhost:5002).`);
                                return;
                              }
                              const data = await res.json();
                              if (!res.ok) {
                                toast.error(data.message || `Request failed (${res.status}). Please try again.`);
                                return;
                              }
                              if (data.success) {
                                setQuestionSubmitted(true);
                                setQuestion('');
                              } else {
                                toast.error(data.message || 'Failed to submit question. Please try again.');
                              }
                            } catch (err) {
                              console.error('Submit question error:', err);
                              toast.error('Failed to submit question. Please try again.');
                            } finally {
                              setQuestionSubmitting(false);
                            }
                          }}
                          disabled={!question.trim() || !currentUser || questionSubmitting}
                        >
                          {questionSubmitting ? (
                            <><FaSpinner className="spinner" /> Sending...</>
                          ) : (
                            'Submit Question'
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="question-success">
                        <div className="success-icon"><FaCheck /></div>
                        <h3>Your question has been sent to the host</h3>
                        <p>
                          The property owner will be notified and will typically respond within 24 hours.
                          You&apos;ll receive a notification when they reply.
                        </p>
                        <button
                          className="btn-ask-another"
                          onClick={() => setQuestionSubmitted(false)}
                        >
                          Ask Another Question
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>



            {/* Availability Section */}
            {availability && availability.available && (
              <motion.section
                className="property-section availability-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h2>Availability</h2>
                <p className="availability-subtitle">
                  {numberOfNights} {numberOfNights === 1 ? 'night' : 'nights'}, {guests} {guests === 1 ? 'guest' : 'guests'}
                </p>

                <div className="rooms-list">
                  {availability.rooms.map((room) => (
                    <div key={room.id} className="room-card">
                      {/* Left: Room Info */}
                      <div className="room-info-left">
                        <h3 className="room-name">{room.name}</h3>
                        <div className="room-details">
                          <span className="room-guests">
                            <FaUsers /> {room.maxGuests} {room.maxGuests === 1 ? 'guest' : 'guests'}
                          </span>
                          {room.bedType && (
                            <span className="room-bed-type">
                              <span className="bed-icon">🛏️</span> {room.bedType}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Middle: Cancellation Info */}
                      <div className="room-info-middle">
                        <div className="cancellation-badge">
                          <FaCheck />
                          <span>Free cancellation</span>
                        </div>
                        <p className="cancellation-detail">
                          {room.cancellationPolicy}
                        </p>
                      </div>

                      {/* Right: Price & Reserve */}
                      <div className="room-booking-right">
                        <div className="room-price">
                          <span className="price-amount">₹{room.totalPrice.toLocaleString()}</span>
                          <span className="price-label">for {numberOfNights} {numberOfNights === 1 ? 'night' : 'nights'}</span>
                          <span className="price-note">+taxes & fees</span>
                        </div>

                        <button 
                          className={`btn-reserve ${reserveLoading ? 'loading' : ''} ${reserveSuccess ? 'success' : ''}`}
                          onClick={handleReserve}
                          disabled={reserveLoading || reserveSuccess}
                        >
                          {reserveSuccess ? (
                            <>
                              <FaCheck /> Reserved!
                            </>
                          ) : reserveLoading ? (
                            <>
                              <FaSpinner className="spinner" /> Processing...
                            </>
                          ) : (
                            'Reserve your stay'
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.section>
            )}

            {availability && !availability.available && (
              <motion.section
                className="property-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="not-available-message">
                  <FaTimes />
                  <h3>Not available for selected dates</h3>
                  <p>Please try different dates</p>
                </div>
              </motion.section>
            )}


          </div>

          {/* Right Column - Booking Summary (Sticky) */}
          <div className="property-right-column">
            {/* Location Map - OpenStreetMap */}
            <div className="property-map-card">
              <h3 className="map-title">Location</h3>
              <PropertyLocationMap 
                property={property}
                height={300}
                showPopup={true}
              />
              <div className="map-address">
                <FaMapMarkerAlt />
                <span>
                  {getSafeString(property.address) 
                    || `${getSafeString(property.city, 'Unknown Location')}${getSafeString(property.state) ? `, ${getSafeString(property.state)}` : ', India'}`}
                </span>
              </div>
            </div>

            {/* How to book - steps card (right side) */}
            {(
              <div className="booking-steps-card">
                <h3 className="booking-steps-title">Book your stay in 4 simple steps</h3>
                <div className="booking-steps-flow">
                  <div className="booking-step">
                    <div className="booking-step-icon-wrap">
                      <FaCalendarAlt className="booking-step-icon" />
                    </div>
                    <span className="booking-step-label">Select dates</span>
                  </div>
                  <div className="booking-step-connector" aria-hidden="true" />
                  <div className="booking-step">
                    <div className="booking-step-icon-wrap">
                      <FaUserFriends className="booking-step-icon" />
                    </div>
                    <span className="booking-step-label">Add guest details</span>
                  </div>
                  <div className="booking-step-connector" aria-hidden="true" />
                  <div className="booking-step">
                    <div className="booking-step-icon-wrap">
                      <FaCreditCard className="booking-step-icon" />
                    </div>
                    <span className="booking-step-label">Review & pay</span>
                  </div>
                  <div className="booking-step-connector" aria-hidden="true" />
                  <div className="booking-step">
                    <div className="booking-step-icon-wrap booking-step-icon-last">
                      <FaCheckCircle className="booking-step-icon" />
                    </div>
                    <span className="booking-step-label">Confirm booking</span>
                  </div>
                </div>
              </div>
            )}

            {/* Login prompt - right side, when user is not logged in */}
            {!currentUser && (
              <div className="login-prompt-card">
                <div className="login-prompt-header">
                  <h3 className="login-prompt-title">Login with Book-Stay and experience more!</h3>
                  <ul className="login-prompt-benefits">
                    <li><FaCheck className="login-prompt-check" /> Save your favourite properties</li>
                    <li><FaCheck className="login-prompt-check" /> Quick checkout & secure payment</li>
                    <li><FaCheck className="login-prompt-check" /> Manage your trips & bookings</li>
                  </ul>
                </div>
                <button
                  type="button"
                  className="login-prompt-cta"
                  onClick={() => setShowLoginModal(true)}
                >
                  Create profile &nbsp;<span className="login-prompt-cta-arrow">›</span>
                </button>
              </div>
            )}

            {(
              <div className="booking-summary-card sticky">
                {/* Discount Badge */}
                {isDiscountVisibleToGuest(property.discount) && (
                  <div className="discount-badge-summary">
                    <span className="discount-badge">{property.discount.badge || 'Special Offer'}</span>
                    {property.discount.title && (
                      <span className="discount-title">{property.discount.title}</span>
                    )}
                  </div>
                )}

                <div className="summary-price">
                  {isDiscountVisibleToGuest(property.discount) ? (
                    <div className="pricing-with-discount">
                      <span className="original-price-summary">₹{property.pricePerNight?.toLocaleString()}</span>
                      <span className="discounted-price-summary">
                        ₹{(() => {
                          const originalPrice = property.pricePerNight || 0;
                          const discount = property.discount;
                          let discountedPrice = originalPrice;
                          
                          if (discount.type === 'percentage') {
                            const discountAmount = (originalPrice * discount.value) / 100;
                            const maxDiscount = discount.maxDiscountAmount || Infinity;
                            const finalDiscountAmount = Math.min(discountAmount, maxDiscount);
                            discountedPrice = originalPrice - finalDiscountAmount;
                          } else if (discount.type === 'fixed') {
                            discountedPrice = Math.max(0, originalPrice - discount.value);
                          }
                          
                          return Math.round(discountedPrice).toLocaleString();
                        })()}
                      </span>
                      <span className="savings-summary">
                        Save {property.discount.type === 'percentage' 
                          ? `${property.discount.value}%` 
                          : `₹${property.discount.value}`}
                      </span>
                    </div>
                  ) : (
                    <span className="price-per-night">₹{property.pricePerNight?.toLocaleString()}</span>
                  )}
                  <span className="per-night-label">per night</span>
                </div>

                {property.discount?.description && (
                  <div className="discount-description">
                    <p>{property.discount.description}</p>
                  </div>
                )}

                <div className="summary-tax-note">
                  <FaInfoCircle />
                  <span>+ ₹{Math.round(((isDiscountVisibleToGuest(property.discount) ? 
                    (() => {
                      const originalPrice = property.pricePerNight || 0;
                      const discount = property.discount;
                      let discountedPrice = originalPrice;
                      
                      if (discount.type === 'percentage') {
                        const discountAmount = (originalPrice * discount.value) / 100;
                        const maxDiscount = discount.maxDiscountAmount || Infinity;
                        const finalDiscountAmount = Math.min(discountAmount, maxDiscount);
                        discountedPrice = originalPrice - finalDiscountAmount;
                      } else if (discount.type === 'fixed') {
                        discountedPrice = Math.max(0, originalPrice - discount.value);
                      }
                      
                      return discountedPrice;
                    })() : property.pricePerNight) || 0) * 0.12).toLocaleString()} taxes and charges per night</span>
                </div>

                <div className="summary-dates">
                  <div className="summary-row clickable-date">
                    <FaCalendarAlt />
                    <div className="date-picker-wrapper-summary">
                      <label>Check-in</label>
                      <div className="date-input-container" onClick={(e) => {
                        const input = e.currentTarget.querySelector('input');
                        if (input) {
                          input.focus();
                          input.click();
                        }
                      }}>
                        <DatePicker
                          selected={checkIn}
                          onChange={(date) => {
                            setCheckIn(date);
                            if (date && checkOut && new Date(checkOut) <= date) {
                              setCheckOut(null);
                            }
                          }}
                          placeholderText="Select date"
                          dateFormat="MMM dd, yyyy"
                          minDate={new Date()}
                          className="summary-date-input"
                          wrapperClassName="summary-date-picker-wrapper"
                          popperClassName="summary-datepicker-popper"
                          popperPlacement="bottom-start"
                          popperModifiers={[
                            {
                              name: "offset",
                              options: {
                                offset: [0, 8]
                              }
                            }
                          ]}
                        />
                        <span className="date-display-text">
                          {checkIn ? checkIn.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Select date'}
                        </span>
                      </div>
                      <div className="time-input-container">
                        <FaClock className="time-icon" />
                        <button
                          type="button"
                          className={`time-select-button time-select-cta ${
                            checkInTime ? 'has-value' : ''
                          }`}
                          onClick={() => setTimePickerOpenFor('checkIn')}
                          aria-label="Select check-in time"
                        >
                          <span className="time-display-text">
                            {formatTimeForDisplay(checkInTime, 'Select check-in time')}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="summary-row clickable-date">
                    <FaCalendarAlt />
                    <div className="date-picker-wrapper-summary">
                      <label>Check-out</label>
                      <div className="date-input-container" onClick={(e) => {
                        const input = e.currentTarget.querySelector('input');
                        if (input) {
                          input.focus();
                          input.click();
                        }
                      }}>
                        <DatePicker
                          selected={checkOut}
                          onChange={(date) => setCheckOut(date)}
                          placeholderText="Select date"
                          dateFormat="MMM dd, yyyy"
                          minDate={checkIn || new Date()}
                          className="summary-date-input"
                          wrapperClassName="summary-date-picker-wrapper"
                          popperClassName="summary-datepicker-popper"
                          popperPlacement="bottom-start"
                          popperModifiers={[
                            {
                              name: "offset",
                              options: {
                                offset: [0, 8]
                              }
                            }
                          ]}
                        />
                        <span className="date-display-text">
                          {checkOut ? checkOut.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Select date'}
                        </span>
                      </div>
                      <div className="time-input-container">
                        <FaClock className="time-icon" />
                        <button
                          type="button"
                          className={`time-select-button time-select-cta ${
                            checkOutTime ? 'has-value' : ''
                          }`}
                          onClick={() => setTimePickerOpenFor('checkOut')}
                          aria-label="Select check-out time"
                        >
                          <span className="time-display-text">
                            {formatTimeForDisplay(checkOutTime, 'Select check-out time')}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="summary-row clickable-guests">
                    <FaUsers />
                    <div className="guest-selector-wrapper-summary">
                      <label>Guests</label>
                      <div className="guest-input-container">
                        <GuestPetSelector
                          onApply={(values) => {
                            setGuestData(values);
                            setGuests(values.adults + values.children);
                          }}
                          initialValues={guestData}
                        />
                        <span className="guests-display-text">
                          {(() => {
                            const parts = [];
                            // Always show adults
                            if (guestData.adults > 0) {
                              parts.push(`${guestData.adults} ${guestData.adults === 1 ? 'Adult' : 'Adults'}`);
                            }
                            // Show children if > 0
                            if (guestData.children > 0) {
                              parts.push(`${guestData.children} ${guestData.children === 1 ? 'Child' : 'Children'}`);
                            }
                            // Show infants if > 0
                            if (guestData.infants > 0) {
                              parts.push(`${guestData.infants} ${guestData.infants === 1 ? 'Infant' : 'Infants'}`);
                            }
                            // Show rooms (always show if set)
                            if (guestData.rooms > 0) {
                              parts.push(`${guestData.rooms} ${guestData.rooms === 1 ? 'Room' : 'Rooms'}`);
                            }
                            // Show pets only if > 0
                            if (guestData.pets > 0) {
                              parts.push(`${guestData.pets} ${guestData.pets === 1 ? 'Pet' : 'Pets'}`);
                            }
                            // Fallback to default if nothing is set
                            return parts.length > 0 ? parts.join(' · ') : '1 Adult · 1 Room';
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {numberOfNights > 0 && (
                  <>
                    <div className="summary-breakdown">
                      <div className="breakdown-row">
                        <span>₹{property.pricePerNight?.toLocaleString()} × {numberOfNights} nights</span>
                        <span>₹{(discountDetails.hasDiscount ? discountDetails.originalPrice : totalPrice).toLocaleString()}</span>
                      </div>
                      
                      {/* Discount Row */}
                      {discountDetails.hasDiscount && (
                        <div className="breakdown-row discount-row">
                          <span className="discount-label">
                            <span className="discount-icon">🎯</span>
                            {discountDetails.discountTitle} ({discountDetails.discountText})
                          </span>
                          <span className="discount-amount">-₹{discountDetails.discountAmount.toLocaleString()}</span>
                        </div>
                      )}
                      
                      {/* Subtotal after discount */}
                      {discountDetails.hasDiscount && (
                        <div className="breakdown-row subtotal-row">
                          <span>Subtotal</span>
                          <span>₹{discountDetails.discountedPrice.toLocaleString()}</span>
                        </div>
                      )}
                      
                      <div className="breakdown-row">
                        <span>Service fee</span>
                        <span>₹{Math.round((discountDetails.hasDiscount ? discountDetails.discountedPrice : totalPrice) * ((property.serviceFeePercent ?? 10) / 100)).toLocaleString()}</span>
                      </div>
                      <div className="breakdown-row">
                        <span>Taxes & charges</span>
                        <span>₹{Math.round((discountDetails.hasDiscount ? discountDetails.discountedPrice : totalPrice) * 0.12).toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Savings Highlight */}
                    {discountDetails.hasDiscount && (
                      <div className="savings-highlight">
                        <span className="savings-text">
                          🎉 You save ₹{discountDetails.discountAmount.toLocaleString()} ({discountDetails.savingsPercentage}%) with this offer!
                        </span>
                      </div>
                    )}

                    <div className="summary-total">
                      <span>Total</span>
                      <span>₹{(() => {
                        const basePrice = discountDetails.hasDiscount ? discountDetails.discountedPrice : totalPrice;
                        const serviceFeePercent = property.serviceFeePercent ?? 10;
                        const serviceFee = Math.round(basePrice * (serviceFeePercent / 100));
                        const taxes = Math.round(basePrice * 0.12);
                        return (basePrice + serviceFee + taxes).toLocaleString();
                      })()}</span>
                    </div>
                  </>
                )}

                {/* Detailed Cancellation Policy */}
                <div className="summary-cancellation-policy">
                  <div className="cancellation-policy-header">
                    <FaCheck />
                    <strong>Free cancellation</strong>
                  </div>
                  <p className="cancellation-policy-detail">
                    Cancel before {checkIn ? new Date(new Date(checkIn).getTime() - 24 * 60 * 60 * 1000).toLocaleDateString() : 'check-in'} 
                    {' '}for a full refund. No cancellation fees.
                  </p>
                </div>

                <button className="btn-reserve-sticky" onClick={handleReserve} disabled={!checkIn || !checkOut}>
                  {checkIn && checkOut ? 'Reserve your stay' : 'Select dates to reserve'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <TimePickerModal
        open={timePickerOpenFor === 'checkIn'}
        title="Select check-in time"
        initialTime={checkInTime}
        helperText="Early check-in subject to availability"
        onClose={() => setTimePickerOpenFor(null)}
        onConfirm={(time) => {
          setCheckInTime(time);
          setTimePickerOpenFor(null);
        }}
      />
      <TimePickerModal
        open={timePickerOpenFor === 'checkOut'}
        title="Select check-out time"
        initialTime={checkOutTime}
        helperText="Late check-out subject to availability"
        onClose={() => setTimePickerOpenFor(null)}
        onConfirm={(time) => {
          setCheckOutTime(time);
          setTimePickerOpenFor(null);
        }}
      />
    </div>
  );
};

export default PropertyDetailsPage;

